import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { EMPTY_VALUE } from '../internalRoles';
import { PROFILE_DATE_TIME_FORMAT } from './dateFormat';

/**
 * The header's name for an identity. Both name halves are nullable in
 * auth-service, so a sparse identity falls back to its email, which is how an
 * admin found it in the first place.
 */
const toName = ({ firstName, lastName, email }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || email || EMPTY_VALUE;

/**
 * A stored phone number as an admin reads it. auth-service stores E.164: a
 * North American number renders as "(305) 555-0123", any other keeps its
 * calling code, and a value that does not parse is shown as stored — it is
 * still the number on the account.
 */
const toPhoneLabel = (phoneNumber) => {
  if (!phoneNumber) return EMPTY_VALUE;

  const parsed = parsePhoneNumberFromString(phoneNumber, { defaultCountry: 'US', extract: false });
  if (!parsed) return phoneNumber;

  return parsed.countryCallingCode === '1' ? parsed.formatNational() : parsed.formatInternational();
};

const toDateTimeLabel = (isoDate) =>
  (isoDate ? moment(isoDate).format(PROFILE_DATE_TIME_FORMAT) : EMPTY_VALUE);

/**
 * Maps one auth-service user onto the Location User profile.
 *
 * Last Login reads `lastActiveAt`, which auth-service moves on every sign-in and
 * token refresh — the closest thing it records to a last login.
 *
 * @param {import('../types/User').IUserSummaryViewEntity} user
 * @returns {import('../types/User').ILocationUserProfile}
 */
export const toLocationUserProfile = (user) => ({
  id: user.id,
  name: toName(user),
  firstName: user.firstName ?? null,
  lastName: user.lastName ?? null,
  email: user.email ?? null,
  phoneNumber: user.phoneNumber ?? null,
  phoneLabel: toPhoneLabel(user.phoneNumber),
  createdAtLabel: toDateTimeLabel(user.createdAt),
  lastLoginLabel: toDateTimeLabel(user.lastActiveAt),
  timeZoneLabel: user.timeZone || EMPTY_VALUE,
});

/**
 * Location User adapters, grouped to match the `UserAdapters` convention.
 */
export const LocationUserAdapters = {
  toLocationUserProfile,
};
